import { Button, Dialog } from '@mui/material';
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Currencies from '../../../../utils/Currencies';
import { Dates as DateUtils } from 'arbitrip-common/client/utils';

function TermsChangedDialog({
    modalIsOpen,
    onBack,
    onConfirm,
    priceChanged,
    oldPrice,
    newPrice,
    deadlineChanged,
    oldCancellationDeadline,
    newCancellationDeadline,
    boardChanged,
    oldBoard,
    newBoard,
    profile,
    t,
    i18n,
}) {
    const formatPrice = (price) =>
        Currencies.getPriceWithDisplayCurrencyByPrecision(
            price,
            profile.display_currency_exchange_rate,
            profile.display_currency,
            0,
        );

    const exchangeRate = profile.display_currency_exchange_rate || 1;
    const displayedOld = Math.round(oldPrice * exchangeRate);
    const displayedNew = Math.round(newPrice * exchangeRate);
    const isPriceIncrease = priceChanged && displayedNew > displayedOld;
    const displayedDelta = priceChanged ? displayedNew - displayedOld : 0;
    const sign = displayedDelta > 0 ? '+' : displayedDelta < 0 ? '-' : '';
    const formattedDelta = priceChanged && displayedDelta !== 0
        ? `${sign}${formatPrice(Math.abs(displayedDelta) / exchangeRate)}`
        : null;

    const hasVisiblePriceChange = priceChanged && displayedDelta !== 0;

    // If nothing meaningful changed at display precision, skip the dialog entirely.
    React.useEffect(() => {
        if (modalIsOpen && !hasVisiblePriceChange && !deadlineChanged && !boardChanged) {
            onConfirm();
        }
    }, [modalIsOpen, hasVisiblePriceChange, deadlineChanged, boardChanged]);

    return (
        <Dialog
            className="reservation-dialog-main"
            open={modalIsOpen}
            aria-describedby="terms-changed-dialog-description"
            fullWidth={true}
        >
            <div className="terms-changed-container" data-testid="terms-changed-dialog">
                <h3 className="terms-changed-title">{t('TermsChangedDialog.Title')}</h3>

                {priceChanged && displayedDelta !== 0 && (
                    <div className="terms-changed-card">
                        <span className="terms-changed-card-label">
                            {isPriceIncrease
                                ? t('TermsChangedDialog.PriceIncreased')
                                : t('TermsChangedDialog.PriceDecreased')}
                        </span>
                        <div className="terms-changed-prices">
                            <span className="terms-changed-price-old">{formatPrice(oldPrice)}</span>
                            <div className="terms-changed-arrow" />
                            <span className="terms-changed-price-new">{formatPrice(newPrice)}</span>
                            <span className={`terms-changed-delta ${isPriceIncrease ? 'increase' : 'decrease'}`}>
                                {formattedDelta}
                            </span>
                        </div>
                    </div>
                )}

                {deadlineChanged && (
                    <div className="terms-changed-card">
                        <span className="terms-changed-card-label">
                            {t('TermsChangedDialog.CancellationTermsChanged')}
                        </span>
                        <div className="terms-changed-deadline-row">
                            <div className="terms-changed-deadline-col">
                                <span className="terms-changed-deadline-label">{t('TermsChangedDialog.Old')}</span>
                                <span className="terms-changed-deadline-value old">
                                    {DateUtils.formatCancellationDeadline(
                                        oldCancellationDeadline,
                                        i18n && i18n.language,
                                    ) || t('ReservationDialog.NonRefundable')}
                                </span>
                            </div>
                            <div className="terms-changed-arrow" />
                            <div className="terms-changed-deadline-col">
                                <span className="terms-changed-deadline-label">{t('TermsChangedDialog.New')}</span>
                                <span className="terms-changed-deadline-value new">
                                    {DateUtils.formatCancellationDeadline(
                                        newCancellationDeadline,
                                        i18n && i18n.language,
                                    ) || t('ReservationDialog.NonRefundable')}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {boardChanged && (
                    <div className="terms-changed-card">
                        <span className="terms-changed-card-label">
                            {t('TermsChangedDialog.BoardChanged')}
                        </span>
                        <div className="terms-changed-deadline-row">
                            <div className="terms-changed-deadline-col">
                                <span className="terms-changed-deadline-label">{t('TermsChangedDialog.Old')}</span>
                                <span className="terms-changed-deadline-value old">{t(oldBoard)}</span>
                            </div>
                            <div className="terms-changed-arrow" />
                            <div className="terms-changed-deadline-col">
                                <span className="terms-changed-deadline-label">{t('TermsChangedDialog.New')}</span>
                                <span className="terms-changed-deadline-value new">{t(newBoard)}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="terms-changed-actions">
                    <Button
                        className="terms-changed-btn-back"
                        variant="outlined"
                        onClick={onBack}
                    >
                        {t('ReservationDialog.BackToHotelButton')}
                    </Button>
                    <Button
                        className="terms-changed-btn-confirm"
                        variant="contained"
                        onClick={onConfirm}
                    >
                        {hasVisiblePriceChange
                            ? t('TermsChangedDialog.ConfirmContinueWithPrice', { price: formatPrice(newPrice) })
                            : t('ReservationDialog.ConfirmContinueButton')}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

TermsChangedDialog.propTypes = {
    modalIsOpen: PropTypes.bool.isRequired,
    onBack: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    priceChanged: PropTypes.bool,
    oldPrice: PropTypes.number,
    newPrice: PropTypes.number,
    deadlineChanged: PropTypes.bool,
    oldCancellationDeadline: PropTypes.string,
    newCancellationDeadline: PropTypes.string,
    boardChanged: PropTypes.bool,
    oldBoard: PropTypes.string,
    newBoard: PropTypes.string,
    profile: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    i18n: PropTypes.shape({ language: PropTypes.string }),
};

export default memo(withTranslation()(TermsChangedDialog));
